import type { Blackboard } from "./Blackboard";
import { tagValue } from "./Blackboard";
import { evaluate, type AstNode } from "./ExprEval";
import { Ternary } from "./Ternary";

const MAX_CASCADE_DEPTH = 100;

export interface RuleDirective {
  key: string;
  ref?: string;
  stmt_ast?: { var: string; expr_ast: AstNode };
}

export interface RuleDefinition {
  name: string;
  cond_ast: AstNode | null;
  triggers: string[];
  directives: RuleDirective[];
}

interface QueueEntry {
  rule: RuleDefinition;
  depth: number;
}

export class RuleEngine {
  private _rules: RuleDefinition[] = [];
  private _triggerMap: Map<string, RuleDefinition[]> = new Map();
  private _firing = false;
  private _paused = false;
  private _queue: QueueEntry[] = [];
  private _unsubBb: (() => void) | null = null;

  register(rules: RuleDefinition[], bb: Blackboard): void {
    this._rules = rules;
    this._triggerMap.clear();

    for (const rule of rules) {
      for (const varName of rule.triggers) {
        const list = this._triggerMap.get(varName);
        if (list) {
          list.push(rule);
        } else {
          this._triggerMap.set(varName, [rule]);
        }
      }
    }

    if (this._unsubBb) this._unsubBb();
    const listener = (key: string) => this.onVarChanged(key, bb);
    bb.listenAll(listener);
    this._unsubBb = () => bb.removeGlobalListener(listener);
  }

  onVarChanged(varName: string, bb: Blackboard): void {
    if (this._paused) return;

    const triggered = this._triggerMap.get(varName);
    if (!triggered || triggered.length === 0) return;

    if (this._firing) {
      for (const rule of triggered) {
        this._queue.push({ rule, depth: 0 });
      }
      return;
    }

    this._firing = true;
    try {
      for (const rule of triggered) {
        this._queue.push({ rule, depth: 0 });
      }
      this._processQueue(bb);
    } finally {
      this._firing = false;
    }
  }

  private _processQueue(bb: Blackboard): void {
    let processed = 0;

    while (this._queue.length > 0) {
      if (processed >= MAX_CASCADE_DEPTH) {
        console.warn(`RuleEngine: max cascading depth (${MAX_CASCADE_DEPTH}) exceeded — stopping`);
        this._queue = [];
        return;
      }

      const entry = this._queue.shift()!;
      processed++;
      this._evaluateRule(entry.rule, entry.depth, bb);
    }
  }

  private _evaluateRule(rule: RuleDefinition, depth: number, bb: Blackboard): void {
    if (!rule.cond_ast) return;

    const result = evaluate(rule.cond_ast, bb);
    const ternary = result instanceof Ternary ? result : Ternary.from(result);

    let matchKey: string;
    if (ternary.isTrue) matchKey = "ontrue";
    else if (ternary.isFalse) matchKey = "onfalse";
    else matchKey = "onunknown";

    for (const directive of rule.directives) {
      if (directive.key !== matchKey) continue;

      if (directive.ref) {
        const refRule = this._rules.find((r) => r.name === directive.ref);
        if (refRule) {
          this._queue.push({ rule: refRule, depth: depth + 1 });
        } else {
          console.warn(`RuleEngine: referenced rule "${directive.ref}" not found`);
        }
      } else if (directive.stmt_ast) {
        const val = evaluate(directive.stmt_ast.expr_ast, bb);
        bb.set(directive.stmt_ast.var, tagValue(val));
      }
    }
  }

  pause(): void {
    this._paused = true;
  }

  resume(): void {
    this._paused = false;
  }

  get isPaused(): boolean {
    return this._paused;
  }

  tick(bb: Blackboard): void {
    if (this._paused) return;

    const autoRules = this._rules.filter((r) => r.triggers.includes("_auto"));
    if (autoRules.length === 0) return;

    this._firing = true;
    try {
      for (const rule of autoRules) {
        this._queue.push({ rule, depth: 0 });
      }
      this._processQueue(bb);
    } finally {
      this._firing = false;
    }
  }

  clear(): void {
    this._rules = [];
    this._triggerMap.clear();
    this._queue = [];
    this._firing = false;
  }
}
